import { EventType } from "@/analytics/analytics-events";
import { selectIsPointCloudViewable } from "@/modes/select-current-area-data";
import { selectActiveCadModel } from "@/store/cad/cad-slice";
import { OnlyIElementsStore } from "@/store/ielements-slice-selector";
import { selectModeName } from "@/store/mode-selectors";
import { changeMode } from "@/store/mode-slice";
import { selectActiveElement } from "@/store/selections-selectors";
import { setActiveElement } from "@/store/selections-slice";
import { RootState } from "@/store/store";
import { SceneFilter } from "@/types/scene-filter";
import {
  selectAncestor,
  selectChildDepthFirst,
  selectIElement,
} from "@faro-lotv/app-component-toolbox";
import { Analytics } from "@faro-lotv/foreign-observers";
import {
  IElement,
  IElementAreaSection,
  IElementGaussianSplats,
  IElementGenericPointCloud,
  IElementTypeHint,
  IElementVideo360,
  isIElementAreaSection,
  isIElementGaussianSplats,
  isIElementGenericImgSheet,
  isIElementGenericPointCloud,
  isIElementGenericPointCloudStream,
  isIElementGenericStream,
  isIElementImg360,
  isIElementModel3dStream,
  isIElementVideo360,
} from "@faro-lotv/ielement-types";
import { Typography } from "@mui/material";
import { ContextMenuActionHandlerArgs } from "./action-types";

/**
 * @returns The scene filter for the input element
 * @param activeElement The element for which we want to know the type
 */
function selectWalkModeSceneFilter(activeElement: IElement | undefined) {
  return (state: RootState): SceneFilter | undefined => {
    if (!activeElement) {
      return;
    }
    if (selectChildDepthFirst(activeElement, isIElementImg360)(state)) {
      return SceneFilter.Pano;
    }
    if (
      selectChildDepthFirst(
        activeElement,
        isIElementGenericPointCloudStream,
      )(state)
    ) {
      return SceneFilter.PointCloud;
    }
    if (selectChildDepthFirst(activeElement, isIElementModel3dStream)(state)) {
      return SceneFilter.Cad;
    }
    // If we reach this point, we are in an empty floor
  };
}

/**
 * Check if a element is currently used in a 3d scene
 *
 * TODO: create generic way of exiting mode when require data for the mode is removed. https://faro01.atlassian.net/browse/SWEB-1334
 *
 * @param element the element to check
 * @param state the current app state
 * @returns true if the element is in use in the 3d view
 */
export function isElementInUse(
  element: IElement | undefined,
  state: RootState,
): boolean {
  const activeElement = selectActiveElement(state);

  const modeName = selectModeName(state);

  if (activeElement?.id === element?.id) {
    return true;
  }

  // Get the stream element contained in the passed section
  const sectionStream = selectChildDepthFirst(
    element,
    isIElementGenericStream,
  )(state);

  // Get the stream element from the active element
  const activeStream = selectChildDepthFirst(
    activeElement,
    isIElementGenericStream,
  )(state);

  // Check if the walk mode overlay element is the same as the section stream that we want to delete
  const walkOverlay = selectActiveCadModel(state);
  if (
    modeName === "walk" &&
    walkOverlay &&
    walkOverlay.id === sectionStream?.id
  ) {
    // The section stream is in use by the walk mode overlay element
    return true;
  }

  // Check if the stream element from the section we want to delete, is the same as the active stream from the active element.
  if (sectionStream !== activeStream) {
    // The section stream is not in use since it's not the same as the active stream
    return false;
  }

  if (
    modeName === "walk" &&
    selectWalkModeSceneFilter(activeElement)(state) !== SceneFilter.Pano
  ) {
    return true;
  }

  // Check if one of the ancestor of the active element is the element we are evaluating
  if (selectAncestor(activeElement, (e) => e.id === element?.id)(state)) {
    return true;
  }

  return modeName === "overview";
}

type RedirectToFloorScalingToolArgs = Pick<
  ContextMenuActionHandlerArgs,
  "elementID" | "state" | "dispatch" | "createDialog"
>;

/**
 * Redirect to the Floor Scale tool, showing a confirmation dialog if the sheet
 * has already a scale
 */
export async function redirectToFloorScalingTool({
  elementID,
  state,
  createDialog,
  dispatch,
}: RedirectToFloorScalingToolArgs): Promise<void> {
  Analytics.track(EventType.openAreaScaleTool);

  // Be sure that the current element is a floor
  const element = selectIElement(elementID)(state);

  // If the element is a generic image sheet, find the area section
  const area = isIElementGenericImgSheet(element)
    ? selectAncestor(element, isIElementAreaSection)(state)
    : element;

  if (!area || !isIElementAreaSection(area)) {
    return;
  }

  // If the sheet has a scale defined, show a dialog
  if (area.pose?.scale) {
    const hasAccepted = await createDialog({
      title: "Set area scale",
      content: (
        <Typography>
          Changing the scale of the area will also affect previous alignments.
          <br />
          <br />
          Point cloud coordinate systems, their geolocations, and other elements
          will remain unchanged.
        </Typography>
      ),
      confirmText: "Set scale",
    });
    // Do nothing if the user cancelled
    if (!hasAccepted) {
      Analytics.track(EventType.cancelAreaScaleWarning);
      return;
    }
    Analytics.track(EventType.confirmAreaScaleWarning);
  }

  dispatch(setActiveElement(elementID));
  dispatch(changeMode("floorscale"));
}

/**
 * @returns the point cloud to download for a specific IElement if available
 * @param el node of the pointcloud IElement container
 * @param predicate to discriminate between multiple available point clouds
 */
export function selectDownloadablePointCloud(
  el?: IElement,
  predicate: (el: IElement) => boolean = () => true,
) {
  return ({
    iElements,
  }: OnlyIElementsStore): IElementGenericPointCloud | undefined =>
    selectChildDepthFirst(
      el,
      (el): el is IElementGenericPointCloud =>
        isIElementGenericPointCloud(el) &&
        predicate(el) &&
        selectIsPointCloudViewable(el)({ iElements }),
    )({ iElements });
}

/**
 * @returns the Gaussian splats PLY file to download for a specific IElement if available
 * @param el node of the dataset container
 */
export function selectDownloadableGaussianSplatsPly(el?: IElement) {
  return ({
    iElements,
  }: OnlyIElementsStore): IElementGaussianSplats | undefined =>
    selectChildDepthFirst(
      el,
      (child): child is IElementGaussianSplats =>
        isIElementGaussianSplats(child) &&
        child.typeHint === IElementTypeHint.gaussianSplatsPly,
    )({ iElements });
}

/**
 * @returns the video walk to download for a specific IElement if available
 * @param el node of the video walk IElement container
 */
export function selectDownloadableVideoWalk(el?: IElement) {
  return ({ iElements }: OnlyIElementsStore): IElementVideo360 | undefined =>
    selectChildDepthFirst(el, isIElementVideo360)({ iElements });
}

/**
 * Returns whether the give area is fully loaded, or only its lightweight elements are available
 *
 * @param area the area to check
 * @param store the current state of the app store
 * @returns whether the give area is fully loaded, or only its lightweight elements are available
 */
export function selectIsAreaLoaded(
  area: IElementAreaSection,
  { iElements }: OnlyIElementsStore,
): boolean {
  return (area.childrenIds ?? []).every(
    (childId) => !!selectIElement(childId)({ iElements }),
  );
}
